import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Apple } from "lucide-react";
import { useTranslation } from "react-i18next";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { homeFor } from "@/lib/roles";

export default function Login() {
  const { login, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const me = await login(email, password);
      toast.success(t("auth.welcomeBack"));
      navigate(homeFor(me.role));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const social = (p) => toast.info(`${p} sign-in is coming soon`);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/radar";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen wo-bg flex items-center justify-center px-5 py-12">
      <div className="absolute inset-0 wo-glow-radial pointer-events-none" />
      <div className="absolute top-4 right-4 z-10"><LanguageSwitcher /></div>
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size="text-3xl" /></div>
        <div className="liquid-glass rounded-2xl p-8">
          <h1 className="font-display font-bold text-2xl text-slate-100 mb-1">{t("auth.welcomeBack")}</h1>
          <p className="text-slate-400 text-sm mb-6">{t("auth.loginSub")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm">{t("auth.email")}</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 bg-slate-950/40 border-white/10 text-slate-100" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">{t("auth.password")}</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 bg-slate-950/40 border-white/10 text-slate-100" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <Button data-testid="auth-login-submit-button" type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 py-6">
              {loading ? "..." : t("auth.loginBtn")}
            </Button>
          </form>

          <div className="mt-6">
            <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
              <div className="h-px flex-1 bg-white/10" /> or <div className="h-px flex-1 bg-white/10" />
            </div>
            <button type="button" onClick={googleLogin} data-testid="login-google"
              className="w-full flex items-center justify-center gap-2.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 py-3 text-sm font-medium mb-2.5 transition-colors">
              <svg width="17" height="17" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22 22-9.8 22-22c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 15.6 2 8.3 6.8 6.3 14.7z"/><path fill="#4CAF50" d="M24 46c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5C29.6 36.6 26.9 38 24 38c-5.2 0-9.6-3.3-11.2-7.9l-6.6 5.1C8.2 41.1 15.5 46 24 46z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5c-.5.4 6.3-4.6 6.3-15 0-1.3-.1-2.3-.4-3.5z"/></svg>
              Continue with Google
            </button>
            <button type="button" onClick={() => social("Apple")} data-testid="login-apple"
              className="w-full flex items-center justify-center gap-2.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 py-3 text-sm font-medium transition-colors">
              <Apple size={17} /> Continue with Apple
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-slate-400">
            {t("auth.noAccount")} <Link to="/register" className="text-teal-400 hover:underline">{t("auth.signup")}</Link>
          </p>
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-500" data-testid="login-legal-links">
          <Link to="/legal#terms" className="hover:text-teal-300 transition-colors">Terms</Link>
          <span className="text-slate-700">·</span>
          <Link to="/legal#privacy" className="hover:text-teal-300 transition-colors">Privacy</Link>
          <span className="text-slate-700">·</span>
          <Link to="/legal#gdpr" className="hover:text-teal-300 transition-colors">GDPR</Link>
        </div>
      </div>
    </div>
  );
}
